/*
 * The sidebar search modal's content, with one fix: an empty query shows a prompt, not a failure.
 *
 * Opening search ran the empty term as a query, got zero results, and rendered "Sorry, no results
 * were found" before the person had typed anything — reporting a search nobody made as a failed one.
 * This is a custom `children` render-prop for `<SidebarSearchModal>` (which renders whatever it is
 * given inside the search context, in place of the default modal). It shows the results branch only
 * once there is a term; until then it invites the person to type.
 *
 * It lives in the scaffold rather than the overlay because `apply.mjs` does not manage the sidebar
 * when a ProductNav is present — Sidebar.tsx and its helpers here are hand-maintained.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useContent } from '@backstage/core-components';
import {
  SearchBar,
  SearchResult,
  SearchResultPager,
  useSearch,
} from '@backstage/plugin-search-react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import { useNavigate } from 'react-router-dom';

export function SearchModalContent({ toggleModal }: { toggleModal: () => void }) {
  const navigate = useNavigate();
  const { focusContent } = useContent();
  const { term } = useSearch();
  const searchBarRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    searchBarRef.current?.focus();
  }, []);

  const goToResults = useCallback(() => {
    const query = searchBarRef.current?.value ?? '';
    navigate(`/search?query=${encodeURIComponent(query)}`);
    setTimeout(focusContent, 0);
    toggleModal();
  }, [navigate, focusContent, toggleModal]);

  const empty = term.trim() === '';

  return (
    <>
      <DialogTitle>
        <Box
          style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}
        >
          <SearchBar inputProps={{ ref: searchBarRef }} onSubmit={goToResults} />
          <IconButton aria-label="close" onClick={toggleModal}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {empty ? (
          <Box style={{ padding: '24px 8px', textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary">
              Start typing to search StackSchematic — applications, services, documentation, and the
              rest of the catalog.
            </Typography>
          </Box>
        ) : (
          <>
            <Grid
              container
              direction="row-reverse"
              justifyContent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <Button
                  color="primary"
                  endIcon={<ArrowForwardIcon />}
                  onClick={goToResults}
                  disableRipple
                >
                  View full results
                </Button>
              </Grid>
            </Grid>
            <Divider />
            <SearchResult onClick={goToResults} onKeyDown={goToResults} />
          </>
        )}
      </DialogContent>
      {empty ? null : (
        <DialogActions style={{ padding: '8px 24px' }}>
          <Grid container direction="row">
            <Grid item xs={12}>
              <SearchResultPager />
            </Grid>
          </Grid>
        </DialogActions>
      )}
    </>
  );
}
